'use client'

import React, { useCallback, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  useGetListingOperatingTimeQuery,
  useLogShareMutation,
} from 'graphql/generated/graphql'
import { buildShareUrl } from '@/lib/sharing'
import Image from 'next/image'
import ProfileRecipes from '@/components/recipe/ProfileRecipes'
import { PiGlobe } from 'react-icons/pi'
import Link from 'next/link'
import ProductList from '@/components/profile/Product'
import VendorList from '@/components/profile/Vendor'
import Heading from '@/components/ui/Heading'
import Locations from '@/components/profile/Locations'
import { Spinner } from 'flowbite-react'
import { ProfileProps } from 'types/common'
import { base64ToFile, sortByDay, transformUserObject } from '@/lib/utils'
import { getFollowerCount } from '@/lib/followerCount'
import { buildProducerDescription, buildMarketDescription } from '@/lib/listingDescription'
import { FollowButton } from '@/components/folllowButton'
import FeedPage from '@/components/recipe/Feed'
import { AuthRequired } from '@/components/authRequired'
import VendorPreview from '@/components/profile/VendorPreview'
import ProductPreview from '@/components/profile/ProductPreview'
import HowToBuy from '@/components/profile/HowToBuy'
import { isUserAvailable } from 'helpers/authUser'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'
import { toast } from 'react-toastify'
import { Share, Crown, Plus, QrCode, X } from 'lucide-react'
import { PageBackButton } from '@/components/ui/page-back-button'
import { uploadImageWithProgress } from '@/lib/uploadImage'
import ProfileEmptyState from '@/components/profile/ProfileEmptyState'
import { VisitorRecipeEmptyState } from '@/components/profile/VisitorRecipeEmptyState'
import { UpgradePaymentModal } from '@/components/modals/upgrade-payment-modal'
import { UpgradeSuccessModal } from '@/components/modals/upgrade-success-modal'
import { FormDataProvider } from '@/hook/useFormData'
import { ProfileShareModal } from '@/components/profile/ProfileShareModal'
import FarmersMarketsModal from '@/components/preview/FarmersMarketsModal'


const GeneralProfile: React.FC<ProfileProps> = ({
  currentUser,
  selectedTab,
  setSelectedTab,
  userDetail,
  listing,
  refetchListing
}) => {
  const router = useRouter()
  const user = useSelector((state: RootState) => state.culineerAuth).user
  const userAvailable = isUserAvailable(user)

  const [image, setImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showCropper, setShowCropper] = useState(false)
  const [progress, setProgress] = useState<number>(0);
  const [completed, setCompleted] = useState<boolean>(false)

  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [isTaxExempt, setIsTaxExempt] = useState(false)
  const [showQR, setShowQR] = useState(false)
  const [qrDownloading, setQrDownloading] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [showEllipsis, setShowEllipsis] = useState(false)
  const [joinPromptOpen, setJoinPromptOpen] = useState(false)

  const apiBase = process.env.NEXT_PUBLIC_API_ENDPOINT?.replace(/\/query$/, '') ?? 'https://dev.culineerapi.com'

  const getUserData = () => {
    return {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      email: user?.email_address || '',
      phone: user?.listing?.phone || user?.listing?.publicPhone || '',
      address: user?.listing?.address || '',
      city: user?.listing?.city || '',
      state: user?.listing?.state || '',
      zip: user?.listing?.zipcode || '',
    }
  }

  const handlePaymentSuccess = (taxExempt: boolean) => {
    setIsTaxExempt(taxExempt)
    setPaymentModalOpen(false)
    setSuccessModalOpen(true)
  }

  const handleSuccessClose = async () => {
    setSuccessModalOpen(false)
    // router.refresh() only re-runs the server component -- it doesn't refetch this
    // page's client-side listingDetail query, which is what actually drives the tier
    // gates shown to ProductList/VendorList/Locations below.
    await refetchListing?.()
    router.refresh()
  }

  const {error, loading, listing: data } = listing
  const [logShare] = useLogShareMutation()

  const rawListingType = data?.listingDetail?.user_type?.toLowerCase()
  const authListingType: 'market' | 'producer' | 'member' | undefined =
    rawListingType === 'market' || rawListingType === 'producer' || rawListingType === 'member'
      ? rawListingType
      : undefined

  // const { data: listing, loading, error } = useListingDetailQuery({
  //   fetchPolicy: 'no-cache',
  //   variables: { slug: userDetail?.userProfile?.default_listing?.slug },
  // })

  const {
    data: OperatingPeriod,
    error: timeError,
    loading: timeloading,
  } = useGetListingOperatingTimeQuery({
    variables: { listingID: data?.listingDetail?.listing_id },
    fetchPolicy: 'no-cache',
  })

  const inputArray = OperatingPeriod?.getListingOperatingTime?.map(
    (op: any) => op
  )
  const sortedArray = inputArray?.sort(sortByDay)

  const dialogRef: any = useRef(null)

  // A logged-out visitor sees a producer's whole profile, but anything they try to use (a product, a tab,
  // Follow, a link) opens the join prompt instead. Share and Back stay usable: shared links are how people
  // arrive here. Capture phase, so it runs before the element's own handler or link navigation.
  const promptJoinOnClick = !userAvailable && authListingType === 'producer'
  const interceptClick = (event: React.MouseEvent) => {
    if (!promptJoinOnClick) return
    const target = event.target as HTMLElement
    // Modals render in a portal outside this wrapper, but React still bubbles their events through it. Without
    // this check the join prompt's own X / buttons (and the share modal) are intercepted and re-open the prompt.
    if (!event.currentTarget.contains(target)) return
    if (target.closest('[data-allow-click]')) return
    if (!target.closest('a, button, [role="button"], [role="tab"], input, select, textarea')) return
    event.preventDefault()
    event.stopPropagation()
    setJoinPromptOpen(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner aria-label="loading" size="xl" color={'success'} />
      </div>
    )
  }

  // if (!userDetail?.userProfile && (error || !data?.listingDetail)) {
  //   return <NotFound />
  // }

  const getEmptyStateImg = (tab: string, membership_type: string, listingType?: string) => {
    const isMarket = listingType === 'market'
    const saladBowl = '/assets/empty-state/premium-recipe-empty-state.svg'
    const recipeBook = '/assets/empty-state/verified-recipe-empty-state.svg'
    const farmer = '/assets/empty-state/producer-empty-state.svg'
    let emptyStateImgUrl
    if(membership_type === 'enhanced') {
      if(tab === 'vendor') {
        emptyStateImgUrl = isMarket ? farmer : '/assets/empty-state/premium-vendor-empty-state.svg'
      } else if(tab === 'product') {
        emptyStateImgUrl = '/assets/empty-state/premium-product-empty-state.svg'
      } else if(tab === 'recipe') {
        emptyStateImgUrl = isMarket ? recipeBook : saladBowl
      } else if(tab === 'location') {
        emptyStateImgUrl = '/assets/empty-state/premium-location-empty-state.svg'
      } else {
        emptyStateImgUrl = '/assets/empty-state/feed-empty-state.svg'
      }
    } else if(membership_type === 'verified business') {
      if(tab === 'vendor') {
        emptyStateImgUrl = isMarket ? farmer : '/assets/empty-state/premium-vendor-empty-state.svg'
      } else if(tab === 'product') {
        emptyStateImgUrl = '/assets/empty-state/premium-product-empty-state.svg'
      } else if(tab === 'recipe') {
        emptyStateImgUrl = isMarket ? recipeBook : saladBowl
      } else if(tab === 'location') {
        emptyStateImgUrl = '/assets/empty-state/premium-location-empty-state.svg'
      } else {
        emptyStateImgUrl = '/assets/empty-state/feed-empty-state.png'
      }
    } else {
      if(tab === 'vendor') {
        emptyStateImgUrl = isMarket ? farmer : '/assets/empty-state/visitor-vendor-empty-state.svg'
      } else if(tab === 'product') {
        emptyStateImgUrl = '/assets/empty-state/visitor-product-empty-state.svg'
      } else if(tab === 'recipe') {
        emptyStateImgUrl = isMarket ? recipeBook : saladBowl
      } else if(tab === 'location') {
        emptyStateImgUrl = '/assets/empty-state/visitor-location-empty-state.svg'
      } else {
        emptyStateImgUrl = '/assets/empty-state/feed-empty-state.png'
      }
    }

    return emptyStateImgUrl
  }

  // `hideButtons` drops the call-to-action: the public producer page shows the empty state with no button.
  const getEmptyState = (tab: string, onAdd?: () => void, options?: { hideButtons?: boolean }) => {
    const membershipType = data?.listingDetail?.membership_type?.toLowerCase() || 'general'
    const isOwner = userDetail?.userProfile?.user_id === currentUser

    if (!isOwner && tab === 'recipe') {
      return (
        <VisitorRecipeEmptyState userProfile={userDetail?.userProfile} />
      )
    }

    let image = getEmptyStateImg(tab, membershipType, data?.listingDetail?.user_type?.toLowerCase())
    if (!isOwner) {
      if (tab === 'product') {
        image = '/assets/empty-state/visitor-product-empty-state.svg'
      } else if (tab === 'location') {
        image = '/assets/empty-state/visitor-location-empty-state.svg'
      } else if (tab !== 'vendor') {
        image = '/assets/empty-state/visitor-feed-empty-state.svg'
      }
    }
    
    let title = ""
    let description = ""
    let buttons: any[] = []

    if (isOwner) {
      const plusIcon = (
        <div className="flex items-center justify-center rounded-full bg-white p-0.5">
          <Plus size={14} className="text-[#85C349]" />
        </div>
      )

      switch (tab) {
        case 'product':
          title = "Your products will appear here"
          description = "Start adding your products to get featured in nearby searches."
          buttons = [{ label: "Add Product", primary: true, icon: plusIcon, onClick: onAdd }]
          break
        case 'vendor':
          title = "No vendors added yet"
          description = "Add vendors to showcase the farmers, producers, and artisans who sell at your market."
          buttons = [{ label: "Add Vendor", primary: true, icon: plusIcon, onClick: onAdd }]
          if (membershipType === 'verified business') {
            buttons.push({
              label: "Upgrade to Pro",
              primary: true,
              onClick: () => setPaymentModalOpen(true),
              icon: <Crown size={16} />
            })
          }
          break
        case 'recipe':
          title = 'No recipes yet'
          description = "Looks like you haven't added any recipes yet"
          buttons = [
            {
              label: 'Add Recipe',
              primary: true,
              icon: plusIcon,
              onClick: onAdd,
            },
          ]
          if (membershipType === 'verified business') {
            buttons.push({
              label: 'Upgrade to Pro',
              primary: true,
              onClick: () => setPaymentModalOpen(true),
              icon: <Crown size={16} />,
            })
          }
          break
        case 'location':
          title = "Your locations will appear here"
          description = "Add your farm or market locations to let customers find you easily."
          buttons = [{ label: "Add Location", primary: true, icon: plusIcon, onClick: onAdd }]
          break
        default:
          title = "Your feed is empty"
          description = "Share your farm updates, seasonal produce, or recipes to engage your audience."
          buttons = [{ label: "Share Update", primary: true, icon: plusIcon, href: "/create-post" }]
      }
    } else {
      // Visitor view
      const name = data?.listingDetail?.name || userDetail?.userProfile?.full_name
      switch (tab) {
        case 'product':
          title = "No products listed right now"
          description = "Check back soon or explore other farms in your area."
          buttons = [
            { label: "Browse Nearby Farms", href: "/search", primary: true }
          ]
          break
        case 'vendor':
          title = "No vendors near you yet"
          description = "We're growing! While we expand to your area, you can stay connected with seasonal ingredients, explore recipes, and engage with the community."
          buttons = [
            { label: "Sign up", href: "/auth/join-culineer" },
            { label: "Browse Nearby Farms", href: "/search", primary: true }
          ]
          break
        case 'location':
          title = "No locations available"
          description = "This farm hasn't shared their locations yet."
          buttons = [{ label: "Browse Nearby Farms", href: "/search", primary: true }]
          break
        default:
          title = "No posts yet"
          description = "Be the first to see updates, seasonal dishes, or farm stories."
          buttons = [
            { 
              customElement: (
                <FollowButton 
                  user={transformUserObject(userDetail?.userProfile)} 
                />
              )
            }
          ]
      }
    }

    return (
      <ProfileEmptyState
        image={image}
        title={title}
        description={description}
        buttons={options?.hideButtons ? [] : buttons}
      />
    )
  }

  const getProfileSrc = () => {
    let profileImgUrl
    if(userDetail?.userProfile?.user_type?.toLowerCase() === 'general') {
      profileImgUrl = userDetail?.userProfile?.profile_photo_resource || '/assets/avatar/user-default.svg'
    } else if (data?.listingDetail?.user_type?.toLowerCase() === 'producer') {
      profileImgUrl = data?.listingDetail?.photo || '/assets/icons/tractor.svg'
    } else if (data?.listingDetail?.user_type?.toLowerCase() === 'market') {
      profileImgUrl = data?.listingDetail?.photo || '/assets/icons/Market.svg'
    }

    return profileImgUrl
  }

  const profileSrc = getProfileSrc()

  const isProducer = userDetail?.userProfile?.user_type?.toLowerCase() === 'producer'

  const isCurrentUser = userDetail?.userProfile?.user_id === currentUser

  const handleShare = async () => {
    try {
      const profileName = data?.listingDetail?.name ?? userDetail?.userProfile?.full_name
      const rawType = userDetail?.userProfile?.user_type?.toLowerCase()
      const contentType = rawType === 'general' ? 'member' : (rawType || 'member')
      const contentId = String(data?.listingDetail?.listing_id || userDetail?.userProfile?.user_id || '')
      const shareUrl = buildShareUrl(window.location.pathname, contentType, contentId, user?.slug)
      const channel = navigator.share ? 'web_share' : 'clipboard'
      if (navigator.share) {
        await navigator.share({ title: `${profileName}'s Profile`, text: `Check out ${profileName}'s profile on Culineer!`, url: shareUrl })
      } else {
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Profile link copied to clipboard')
      }
      if (user?.slug) {
        logShare({ variables: { input: { content_type: contentType, content_id: contentId, share_channel: channel, ref_slug: user.slug } } }).catch(() => {})
      }
    } catch (err) {
      console.error('Sharing failed:', err)
      toast.error('Failed to share profile')
    }
  }

  const handleCroppedImage = useCallback(
    (croppedImageUrl: string) => {
      const profile_name: string = `${data?.listingDetail?.name}`
      const filename = profile_name
        ? `${profile_name}.png`.replace(' ', '-')
        : `${data?.listingDetail?.user?.full_name.replaceAll(' ', '-')}.png`
      const formattedFile = base64ToFile(croppedImageUrl, filename)
      setImage(croppedImageUrl)
      setShowCropper(false)
      uploadListingAvatar(formattedFile)
    },
    []
  )

  const uploadListingAvatar = async (imgFile: File) => {
    try {
      const res = await uploadImageWithProgress({
        file: imgFile,
        listingID: data?.listingDetail?.listing_id!,
        onProgress: (percent) => {
          setProgress(percent)
          if(percent === 100) {
            setCompleted(prev => !prev)
            toast.success("Profile photo updated")
          }
        },
      });
    } catch (err) {
      console.error("Upload failed", err);
      toast.error("Upload falied")
    }
  }

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0]
        const reader = new FileReader()
        reader.onload = (event) => {
          if (event.target) {
            setImage(event.target.result as string)
            setShowCropper(true)
          }
        }
        reader.readAsDataURL(file)
      }
    },
    []
  )

  // QR code values — computed once outside JSX
  const isGeneralUser = userDetail?.userProfile?.user_type?.toLowerCase() === 'general'
  const qrListingSlug = userDetail?.userProfile?.default_listing?.slug
  const qrMemberSlug = userDetail?.userProfile?.slug
  const qrProfileSlug = isGeneralUser ? qrMemberSlug : qrListingSlug
  const qrListingName = isGeneralUser
    ? (userDetail?.userProfile?.full_name || 'Profile')
    : (data?.listingDetail?.name || userDetail?.userProfile?.full_name || 'Listing')
  const qrEndpoint = isGeneralUser ? 'member' : ''
  // QR codes are static/printable and scanned by anyone, not a personal
  // "share" action -- always the same image for a given profile, no
  // per-viewer ref/attribution.
  const qrSrc = qrProfileSlug
    ? (qrEndpoint
        ? `${apiBase}/og/qr/${qrEndpoint}/${qrProfileSlug}.png`
        : `${apiBase}/og/qr/${qrProfileSlug}.png`)
    : null
  const qrFilename = qrListingName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-qr.png'
  const qrContentType = isGeneralUser ? 'member' : (userDetail?.userProfile?.user_type?.toLowerCase() || 'member')
  const qrContentId = String(data?.listingDetail?.listing_id || userDetail?.userProfile?.user_id || '')

  // Plain <a href download> silently fails for cross-origin URLs (qrSrc
  // points at the API domain, not this app's own domain) -- browsers ignore
  // the download attribute in that case and just try to open the image
  // instead. Fetch it as a blob and download that instead, same pattern as
  // PostShareModal's downloadIgImage.
  const downloadQr = async () => {
    if (!qrSrc) return
    setQrDownloading(true)
    try {
      const res = await fetch(qrSrc)
      if (!res.ok) throw new Error('Failed to fetch QR code')
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = qrFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
      if (user?.slug) {
        logShare({ variables: { input: { content_type: qrContentType, content_id: qrContentId, share_channel: 'qr', ref_slug: user.slug } } }).catch(() => {})
      }
    } catch {
      toast.error('Failed to download QR code')
    } finally {
      setQrDownloading(false)
    }
  }

  return (
    <div className="h-full w-full overflow-y-auto">
      <div
        className="container mx-auto max-w-[1536px] pb-6 sm:px-5 xs:px-6 p-3 md:px-4 md:py-4"
        onClickCapture={interceptClick}
      >

      {/* ── Top action row ── */}
      <div className="flex items-center justify-between gap-3 pt-4 pb-3 sm:pt-6">
        <div data-allow-click>
          <PageBackButton />
        </div>
        <div className="sm:hidden flex items-center gap-2">
          {isCurrentUser && qrSrc && (
            <button
              onClick={() => setShowQR(true)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-[#548828]"
              aria-label="QR Code"
            >
              <QrCode size={17} />
            </button>
          )}
          <button
            onClick={() => setShareModalOpen(true)}
            data-allow-click
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-[#548828]"
            aria-label="Share profile"
          >
            <Share size={17} />
          </button>
        </div>
      </div>

      <div className="py-4 sm:py-8">

        {/* ── Profile header ── */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:py-4 sm:pr-3">

          {/* Photo + details — always side-by-side row */}
          <div className="flex flex-row gap-3 sm:space-x-4 w-full sm:w-auto items-start">

            {/* Profile photo — 112px mobile, 160px desktop */}
            <div className="h-28 w-28 sm:h-[160px] sm:w-[160px] flex-shrink-0 overflow-hidden rounded-full bg-gray-100">
              <Image
                src={getProfileSrc() || '/assets/avatar/user-default.svg'}
                alt={data?.listingDetail?.name || userDetail?.userProfile?.full_name || 'Profile photo'}
                width={160}
                height={160}
                className="h-full w-full rounded-full object-cover"
              />
            </div>

            {/* Name block: badges + name + city (+ desktop followers/social/follow) */}
            <div className="flex flex-col gap-1 sm:gap-2 min-w-0">
              {/* Badges: account type + enhanced */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {data?.listingDetail?.user_type && (
                  <span className="rounded-2xl bg-[#4A90E2] px-3 py-1 text-[12px] font-medium text-white whitespace-nowrap">
                    {userDetail?.userProfile?.user_type === 'General' ? 'Member' : data?.listingDetail?.user_type}
                  </span>
                )}
              </div>
              <h1 className="text-lg sm:text-[24px] font-bold sm:font-semibold leading-tight">
                {data?.listingDetail?.name || ''}
              </h1>
              {(data?.listingDetail?.city || data?.listingDetail?.state) && (
                <p className="text-sm text-[#6B6B6B]">
                  {data?.listingDetail?.city}{data?.listingDetail?.city && data?.listingDetail?.state ? ', ' : ''}{data?.listingDetail?.state}
                </p>
              )}

              {/* Desktop-only: followers, social, follow inside name column */}
              <div className="hidden sm:flex sm:flex-col sm:gap-2">
                <div className="ml-0.5 flex items-center space-x-4 text-[#6B6B6B]">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{getFollowerCount(userDetail?.userProfile)}</span>
                    <span className="text-sm">followers</span>
                  </div>
                  <div className="h-1.5 w-1.5 rounded-full bg-[#E6E6E6]" />
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{userDetail?.userProfile?.followings?.length || 0}</span>
                    <span className="text-sm">following</span>
                  </div>
                </div>
                {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
                  data?.listingDetail?.user_type?.toLowerCase() === 'farm' ||
                  data?.listingDetail?.user_type?.toLowerCase() === 'market') &&
                  (data?.listingDetail?.website || data?.listingDetail?.instagram ||
                   data?.listingDetail?.facebook || data?.listingDetail?.twitter) && (
                  <div className="flex flex-row items-center gap-3 py-2">
                    {data?.listingDetail?.website && <Link href={data?.listingDetail?.website} target="__blank"><PiGlobe size={24} /></Link>}
                    {data?.listingDetail?.instagram && <Link href={data?.listingDetail?.instagram} target="__blank"><Image src="/assets/icons/instagram.svg" sizes="100vw" width={24} height={24} alt="Instagram" style={{ width: '100%', height: 'auto' }} /></Link>}
                    {data?.listingDetail?.facebook && <Link href={data?.listingDetail?.facebook} target="__blank"><Image src="/assets/icons/facebook.svg" sizes="100vw" width={24} height={24} alt="Facebook" style={{ width: '100%', height: 'auto' }} /></Link>}
                    {data?.listingDetail?.twitter && <Link href={data?.listingDetail?.twitter} target="__blank"><Image src="/assets/icons/twitter.svg" sizes="100vw" width={24} height={24} alt="Twitter" style={{ width: '100%', height: 'auto' }} /></Link>}
                  </div>
                )}
                {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
                  data?.listingDetail?.user_type?.toLowerCase() === 'farm' ||
                  data?.listingDetail?.user_type?.toLowerCase() === 'market' ||
                  userDetail?.userProfile?.user_type?.toLowerCase() === 'general') &&
                  data?.listingDetail?.user?.user_id !== currentUser && (
                  <div>
                    {/* @ts-ignore */}
                    <FollowButton user={transformUserObject(userDetail?.userProfile)} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile-only: followers, social, follow — below photo+name row */}
          <div className="sm:hidden flex flex-col gap-3 mt-4 w-full">
            <div className="flex items-center space-x-4 text-[#6B6B6B]">
              <div className="flex items-center space-x-2">
                <span className="font-medium">{getFollowerCount(userDetail?.userProfile)}</span>
                <span className="text-sm">followers</span>
              </div>
              <div className="h-1.5 w-1.5 rounded-full bg-[#E6E6E6]" />
              <div className="flex items-center space-x-2">
                <span className="font-medium">{userDetail?.userProfile?.followings?.length || 0}</span>
                <span className="text-sm">following</span>
              </div>
            </div>
            {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
              data?.listingDetail?.user_type?.toLowerCase() === 'farm' ||
              data?.listingDetail?.user_type?.toLowerCase() === 'market') &&
              (data?.listingDetail?.website || data?.listingDetail?.instagram ||
               data?.listingDetail?.facebook || data?.listingDetail?.twitter) && (
              <div className="flex flex-row flex-wrap items-center gap-3">
                {data?.listingDetail?.website && <Link href={data?.listingDetail?.website} target="__blank"><PiGlobe size={24} /></Link>}
                {data?.listingDetail?.instagram && <Link href={data?.listingDetail?.instagram} target="__blank"><Image src="/assets/icons/instagram.svg" sizes="100vw" width={24} height={24} alt="Instagram" style={{ width: '100%', height: 'auto' }} /></Link>}
                {data?.listingDetail?.facebook && <Link href={data?.listingDetail?.facebook} target="__blank"><Image src="/assets/icons/facebook.svg" sizes="100vw" width={24} height={24} alt="Facebook" style={{ width: '100%', height: 'auto' }} /></Link>}
                {data?.listingDetail?.twitter && <Link href={data?.listingDetail?.twitter} target="__blank"><Image src="/assets/icons/twitter.svg" sizes="100vw" width={24} height={24} alt="Twitter" style={{ width: '100%', height: 'auto' }} /></Link>}
              </div>
            )}
            {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
              data?.listingDetail?.user_type?.toLowerCase() === 'farm' ||
              data?.listingDetail?.user_type?.toLowerCase() === 'market' ||
              userDetail?.userProfile?.user_type?.toLowerCase() === 'general') &&
              data?.listingDetail?.user?.user_id !== currentUser && (
              <div className="w-full [&>button]:w-full [&>button]:justify-center">
                {/* @ts-ignore */}
                <FollowButton user={transformUserObject(userDetail?.userProfile)} />
              </div>
            )}
          </div>

          {/* Desktop-only: Share + QR buttons */}
          <div className="hidden sm:flex items-center justify-center space-x-2 pr-3 self-start pt-2">
            <button
              className="rounded-full border border-[#548828] ml-2"
              data-allow-click
              onClick={() => setShareModalOpen(true)}
            >
              <div className="flex items-center justify-center space-x-2 px-3 py-1.5">
                <Share className="text-[#548828] w-5 h-5" />
                <span className="text-[#548828] text-[12px]">Share</span>
              </div>
            </button>
            {userAvailable && (userDetail?.userProfile?.default_listing?.slug || userDetail?.userProfile?.slug) && (
              <button
                className="rounded-full border border-[#548828] w-9 h-9 flex items-center justify-center"
                title="Get QR code"
                onClick={() => setShowQR(true)}
              >
                <QrCode className="text-[#548828] w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* QR modal */}
        {showQR && qrSrc && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowQR(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 flex flex-col items-center gap-4 max-w-xs w-full mx-4 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between w-full">
                <h3 className="text-base font-semibold text-[#222]">{qrListingName} QR Code</h3>
                <button onClick={() => setShowQR(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <img src={qrSrc} alt={`QR code for ${qrListingName}`} className="w-48 h-48 rounded-lg" />
              <p className="text-sm text-gray-500 text-center">
                Print it and put it anywhere customers are — they&apos;ll scan straight to your listing.
              </p>
              <button
                onClick={downloadQr}
                disabled={qrDownloading}
                className="w-full text-center py-2.5 bg-primary text-white rounded-full text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {qrDownloading ? 'Downloading…' : 'Download PNG'}
              </button>
            </div>
          </div>
        )}

        {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
          data?.listingDetail?.user_type?.toLowerCase() === 'farm' ||
          data?.listingDetail?.user_type?.toLowerCase() === 'market') && (
          <div className="pt-3">
            <HowToBuy
              listingId={data?.listingDetail?.listing_id}
              businessName={data?.listingDetail?.name}
              salesChannels={data?.listingDetail?.SalesChannels}
              isOwner={isCurrentUser}
              onUpdated={refetchListing}
            />
          </div>
        )}
        <div className="pt-3 pb-2">
          {userDetail?.userProfile?.user_type.toLowerCase() === 'general' ? (
            <p>{userDetail?.userProfile?.user_bio}</p>
          ) : (
            // Thin/empty listings still get real descriptive text here (same
            // fallback used for the meta description) instead of an empty
            // paragraph — that was the actual cause of pages getting flagged
            // for low word count.
            <p>
              {data?.listingDetail?.description ||
                (data?.listingDetail?.user_type?.toLowerCase() === 'market'
                  ? buildMarketDescription(data?.listingDetail)
                  : buildProducerDescription(data?.listingDetail))}
            </p>
          )}
        </div>
        {(data?.listingDetail?.user_type?.toLowerCase() === 'producer' ||
          data?.listingDetail?.user_type?.toLowerCase() === 'farm') && (
          <div className="flex w-full flex-row flex-wrap space-x-4 overflow-x-auto">
            <div className="mb-3 flex">
              {data?.listingDetail?.Practices?.map(
                ({ imgUrl, name }: any, index: any) => (
                  <Image
                    alt={name}
                    key={index}
                    src={imgUrl}
                    width={40}
                    height={40}
                    className={`m-1 ${index !== 0 ? `z-[${index}]` : ''}`}
                  />
                )
              )}
            </div>
          </div>
        )}
        {data?.listingDetail?.user_type?.toLowerCase() !== 'producer' &&
          (sortedArray && sortedArray.length > 0 || data?.listingDetail?.address || data?.listingDetail?.city) && (
          <div className="mt-5 flex flex-col gap-3">
            <div className="flex items-start gap-2 text-sm text-[#6B6B6B]">
              <span className="font-medium text-[#202020] whitespace-nowrap">Hours & Location:</span>
              {(data?.listingDetail?.address || data?.listingDetail?.city) && (
                <span>
                  {[
                    data?.listingDetail?.address,
                    data?.listingDetail?.city,
                    data?.listingDetail?.state,
                  ].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            {sortedArray && sortedArray.length > 0 && (
              <div className="grid grid-cols-2 flex-wrap gap-4 xs:grid-cols-1 flex-shrink-0">
                {sortedArray?.map((operationPeriod, index) => (
                  <span className="text-sm text-[#6B6B6B]" key={index}>
                    {operationPeriod?.day}: ({operationPeriod?.openingTime} -{' '}
                    {operationPeriod?.closingTime})
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="w-full pb-7 pt-6">
          <div className="flex w-full border-b-2 border-[#D2D2D2] pb-2">
            <div className="-mb-2.5 space-x-6">
              {data?.listingDetail?.user_type?.toLowerCase() ===
                'producer' && (
                <button
                  className={
                    selectedTab === 'products'
                      ? 'border-b-[4px] border-[#6EAC36] px-1 pb-1 font-normal text-[#202020]'
                      : 'text-[#6B6B6B]'
                  }
                  onClick={() => setSelectedTab('products')}
                >
                  Products
                </button>
              )}
              {data?.listingDetail?.user_type?.toLowerCase() ===
                'market' && (
                <button
                  className={
                    selectedTab === 'vendors'
                      ? 'border-b-[4px] border-[#6EAC36] px-1 pb-1 font-normal text-[#202020]'
                      : 'text-[#6B6B6B]'
                  }
                  onClick={() => setSelectedTab('vendors')}
                >
                  Vendors
                </button>
              )}
              <button
                className={
                  selectedTab === 'recipes'
                    ? 'border-b-[4px] border-[#6EAC36] px-1 pb-1 font-normal text-[#202020]'
                    : 'text-[#6B6B6B]'
                }
                onClick={() => setSelectedTab('recipes')}
              >
                Recipes
              </button>
              {/* <button className={selectedTab === 'cookbook' ? 'text-[#202020] font-normal border-b-[4px] pb-1 border-[#6EAC36] px-1' : 'text-[#6B6B6B]'} onClick={() => setSelectedTab('cookbook')}>Cookbook</button> */}
              {/* {user?.user_type?.toLowerCase() === 'producer' && <button className={selectedTab === 'certifications' ? 'text-[#202020] font-normal border-b-[4px] pb-1 border-[#6EAC36] px-1' : 'text-[#6B6B6B]'} onClick={() => setSelectedTab('certifications')}>Certifications</button>} */}
              {data?.listingDetail?.user_type?.toLowerCase() ===
                'producer' && (
                <button
                  className={
                    selectedTab === 'locations'
                      ? 'border-b-[4px] border-[#6EAC36] px-1 pb-1 font-normal text-[#202020]'
                      : 'text-[#6B6B6B]'
                  }
                  onClick={() => setSelectedTab('locations')}
                >
                  Locations
                </button>
              )}
              {(data?.listingDetail?.user_type?.toLowerCase() ===
                'producer' ||
                data?.listingDetail?.user_type?.toLowerCase() ===
                  'market') && (
                <button
                  className={
                    selectedTab === 'feed'
                      ? 'border-b-[4px] border-[#6EAC36] px-1 pb-1 font-normal text-[#202020]'
                      : 'text-[#6B6B6B]'
                  }
                  onClick={() => setSelectedTab('feed')}
                >
                  Feed
                </button>
              )}
            </div>
          </div>

          {userAvailable ? (
            <>
              {selectedTab === 'products' && (
                <ProductList
                  listing_id={data?.listingDetail?.listing_id}
                  user_id={data.listingDetail?.user_id}
                  listingDetail={data?.listingDetail}
                  renderEmptyState={(onAdd) => getEmptyState('product', onAdd)}
                />
              )}
              {selectedTab === 'vendors' && (
                <VendorList
                  listingId={data?.listingDetail?.listing_id}
                  currentUser={currentUser}
                  userId={userDetail?.userProfile?.user_id}
                  listingDetail={data?.listingDetail}
                  renderEmptyState={(onAdd) => getEmptyState('vendor', onAdd)}
                />
              )}
              {selectedTab === 'recipes' && <ProfileRecipes
                owner={isCurrentUser}
                userID={userDetail?.userProfile?.user_id}
                listingID={data?.listingDetail?.listing_id}
                slug={userDetail?.userProfile?.slug}
                userType={userDetail?.userProfile?.user_type || data?.listingDetail?.user_type}
                renderEmptyState={(onAdd) => getEmptyState('recipe', onAdd)}
              />}
              {/* {selectedTab === 'cookbook' && <ProfileCookbook />} */}
              {/* {selectedTab === 'certifications' && <Certifications />} */}
              {selectedTab === 'locations' && (
                <Locations
                  listingId={data?.listingDetail?.listing_id}
                  owner={data?.listingDetail?.user_id}
                  renderEmptyState={(onAdd) => getEmptyState('location', onAdd)}
                  refetchParentListing={refetchListing}
                />
              )}
              {selectedTab === 'feed' && (
                <FeedPage 
                  userId={userDetail?.userProfile?.user_id} 
                  listingID={data?.listingDetail?.listing_id} 
                  renderEmptyState={() => getEmptyState('feed')}
                />
              )}
            </>
          ) : (
            <>
              {selectedTab === 'vendors' && (
                <VendorPreview
                  listingId={data?.listingDetail?.listing_id}
                  listingName={data?.listingDetail?.name || userDetail?.userProfile?.full_name}
                  followerCount={getFollowerCount(userDetail?.userProfile)}
                />
              )}
              {selectedTab === 'products' && (
                <ProductPreview
                  listingId={data?.listingDetail?.listing_id}
                  listingName={data?.listingDetail?.name || userDetail?.userProfile?.full_name}
                  listingType={authListingType === 'producer' ? 'producer' : 'market'}
                  followerCount={getFollowerCount(userDetail?.userProfile)}
                  showAll={authListingType === 'producer'}
                  renderEmpty={() => getEmptyState('product', undefined, { hideButtons: true })}
                />
              )}
              {selectedTab !== 'vendors' && selectedTab !== 'products' && (
                <AuthRequired
                  listingName={data?.listingDetail?.name || userDetail?.userProfile?.full_name}
                  listingType={authListingType}
                  tab={selectedTab}
                  followerCount={getFollowerCount(userDetail?.userProfile)}
                />
              )}
            </>
          )}
        </div>
      </div>

      <FormDataProvider>
        <UpgradePaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          onSuccess={handlePaymentSuccess}
          userData={getUserData()}
        />
      </FormDataProvider>

      <UpgradeSuccessModal
        open={successModalOpen}
        onClose={handleSuccessClose}
        userSlug={userDetail?.userProfile?.slug || ''}
        listingSlug={userDetail?.userProfile?.default_listing?.slug}
        userType={userDetail?.userProfile?.user_type?.toLowerCase()}
        isTaxExempt={isTaxExempt}
      />

      <FarmersMarketsModal
        isOpen={joinPromptOpen}
        onClose={() => setJoinPromptOpen(false)}
        title={`Join Culineer to see more of ${data?.listingDetail?.name || 'this farm'}`}
        subtitle="Follow farms, see where to buy, and find recipes made with local ingredients. It's free."
      />

      <ProfileShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        profile={{
          name: data?.listingDetail?.listing_name || userDetail?.userProfile?.first_name || '',
          profilePath: typeof window !== 'undefined' ? window.location.pathname : '',
          userType: userDetail?.userProfile?.user_type || 'general',
          listingId: data?.listingDetail?.listing_id,
          userId: userDetail?.userProfile?.user_id,
        }}
        userSlug={user?.slug}
      />
      </div>
    </div>
  )
}

export default GeneralProfile
